// ============================================================
// skillTiers - the arithmetic of skill training, pure. One home for both
// hosts of the sheet (the live tab and the library editor).
// ============================================================
import type { Character, SkillId, SkillRating } from './types';
import { computeSkillMirrorBonus, getSkillRating } from './normalize';

export type SkillTier = 'trained' | 'plus10' | 'plus20';

/**
 * What setting one tier writes. Cumulative-tier hygiene: +20 ⇒ +10 ⇒
 * trained; unchecking cascades down. Field order matches the order the
 * fields were always written in, so a new rating serialises the same.
 */
const TIER_WRITES: Record<SkillTier, (value: boolean) => Partial<SkillRating>> = {
  trained: (value) => (value ? { trained: true } : { trained: false, plus10: false, plus20: false }),
  plus10: (value) => (value ? { plus10: true, trained: true } : { plus10: false, plus20: false }),
  plus20: (value) => (value ? { plus20: true, plus10: true, trained: true } : { plus20: false }),
};

/** `rating` with one tier set or cleared, and the others kept consistent with it. */
export function applyTier(rating: SkillRating, tier: SkillTier, value: boolean): SkillRating {
  return { ...rating, ...TIER_WRITES[tier](value) };
}

/**
 * The number a skill tests at. Trained: the characteristic plus every
 * tier, talent and mod. Untrained: half the characteristic (bonuses
 * excluded) for a basic skill, and 0 for an advanced one, which cannot
 * be tested untrained.
 */
export function skillTotal(rating: SkillRating, charValue: number, talentBonus: number, advanced: boolean): number {
  if (rating.trained) {
    return charValue + (rating.plus10 ? 10 : 0) + (rating.plus20 ? 10 : 0) + talentBonus + (rating.mod || 0);
  }
  return advanced ? 0 : Math.floor(charValue / 2);
}

/** What each training level is called, by tierLevel. */
export const TIER_LABELS = ['Untrained', 'Trained', '+10', '+20'];

/** How far a skill is trained, as one number: 0 untrained, 1 trained, 2 +10, 3 +20. */
export function tierLevel(rating: SkillRating): number {
  return [rating.trained, rating.plus10, rating.plus20].filter(Boolean).length;
}

/** `rating` trained to exactly `level` (see tierLevel); the tiers stay cumulative. */
export function withTierLevel(rating: SkillRating, level: number): SkillRating {
  return { ...rating, trained: level >= 1, plus10: level >= 2, plus20: level >= 3 };
}

/** Skills whose rating carries the deprecated `bonus` mirror for stale clients. */
const MIRRORED: ReadonlySet<SkillId> = new Set<SkillId>(['dodge', 'parry']);

/**
 * The sheet's partial for one skill's new rating. Dodge and parry keep
 * their `bonus` mirror in step, as they always have.
 */
export function skillPatch(
  character: Character,
  skillId: SkillId,
  change: (rating: SkillRating) => SkillRating,
): Partial<Character> {
  const next = change(getSkillRating(character, skillId));
  const rating = MIRRORED.has(skillId) ? { ...next, bonus: computeSkillMirrorBonus(next) } : next;
  return { skills: { ...character.skills, [skillId]: rating } };
}

/**
 * The sheet's partial for a skill switched on or off. "Can dodge" is the
 * Dodge skill being trained; untraining clears its tiers, which are
 * cumulative.
 */
export function trainedPatch(character: Character, skill: SkillId, trained: boolean): Partial<Character> {
  return skillPatch(character, skill, (rating) => applyTier(rating, 'trained', trained));
}
